import React, { useCallback, useEffect, useState } from 'react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../context/AudioContext';
import { useCaptions } from '../context/CaptionsContext';
import { useUser } from '../context/UserContext';
import { useNavigation } from '../context/NavigationContext';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import SlideControls from './SlideControls';
import WelcomeStartScreen from './WelcomeStartScreen';

// Import stakeholder slides
import PresenterBioSlide from '../stakeholder/slides/PresenterBioSlide';
import ThreeDimensionsSlide from '../stakeholder/slides/ThreeDimensionsSlide';
import ResilienceDeepDive from '../stakeholder/slides/ResilienceDeepDive';
import CultureDeepDive from '../stakeholder/slides/CultureDeepDive';
import WorkplaceDeepDive from '../stakeholder/slides/WorkplaceDeepDive';
import AICompanionSlide from '../stakeholder/slides/AICompanionSlide';
import UserJourneySlide from '../stakeholder/slides/UserJourneySlide';
import JIMIVideoTransition from '../stakeholder/slides/JIMIVideoTransition';
import CrowdsourcingSlide from '../stakeholder/slides/CrowdsourcingSlide';
import GovernanceSlide from '../stakeholder/slides/GovernanceSlide';
import DataScienceSlide from '../stakeholder/slides/DataScienceSlide';
import ImplementationSlide from '../stakeholder/slides/ImplementationSlide';
// import TimelineSlide from '../stakeholder/slides/TimelineSlide';
import BudgetSlide from '../stakeholder/slides/BudgetSlide';
import ROISlide from '../stakeholder/slides/ROISlide';
// import AdoptionROISlide from '../stakeholder/slides/AdoptionROISlide';
import CostBenefitConclusionSlide from '../stakeholder/slides/CostBenefitConclusionSlide';
import NextStepsSlide from '../stakeholder/slides/NextStepsSlide';
import QASlide from '../stakeholder/slides/QASlide';
import ThankYouSlide from '../stakeholder/slides/ThankYouSlide';

// Import components
import NeuralNetworkBackground from './NeuralNetworkBackground';
import SpeechControls from './SpeechControls';
import ClosedCaptions from './ClosedCaptions';
import AICompanionDemo from './AICompanionDemo';

// Import narration script
import { narrationScript } from '../data/narrationScript';

// Define interfaces
interface VideoControlContextType {
  isVideoPlaying: boolean;
  toggleVideoPlayback: () => void;
  skipVideo: () => void;
  videoProgress: number;
  isVideoSlide: boolean;
}

interface SlideComponentProps {
  isSpeaking: boolean;
  showCaptions: boolean;
  captionsText: string;
  onNext?: () => void;
  onPrevious?: () => void;
  onOpenJIMIChat?: () => void;
  onHelp?: () => void;
  details?: {
    uiSample: 'resilience' | 'culture' | 'efficiency' | 'ai-companion' | 'crowdsourcing' | 'governance';
  };
  mode?: 'standalone' | 'deck';
  onStart?: () => void;
  onNavigateToSlide?: (slideId: string) => void;
  isTransitioning?: boolean;
}

interface Slide {
  id: string;
  title: string;
  component: React.ComponentType<SlideComponentProps>;
  details?: {
    uiSample: 'resilience' | 'culture' | 'efficiency' | 'ai-companion' | 'crowdsourcing' | 'governance';
  };
}

interface StakeholderDeckProps {
  onBackToWelcome?: () => void;
  isWelcomeVisible?: boolean;
  presentationStarted?: boolean;
  startingSlideId?: string | null;
}

interface JIMIChatProps {
  isOpen: boolean;
  onClose: () => void;
  slideData: Array<{ id: string; title: string }>;
  currentSlideIndex: number;
}

interface SlideControlsProps {
  currentSlide: number;
  totalSlides: number;
  onPrevious: () => void;
  onNext: () => void;
  onSlideSelect: (index: number) => void;
  isSpeaking: boolean;
  onToggleSpeech: () => void;
  showCaptions: boolean;
  onToggleCaptions: () => void;
  isWelcomeScreen: boolean;
  isMuted: boolean;
  toggleMute: () => void;
  slides: Slide[];
  onHome: () => void;
  onHelp: () => void;
}

// Define slides
const slides: Slide[] = [
  {
    id: "welcome-start",
    title: "Welcome Start",
    component: WelcomeStartScreen,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "presenter-bio",
    title: "Presenter Bio",
    component: PresenterBioSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "three-dimensions",
    title: "Three Dimensions",
    component: ThreeDimensionsSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "resilience",
    title: "Resilience",
    component: ResilienceDeepDive,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "culture-deepdive",
    title: "Culture Deep Dive",
    component: CultureDeepDive,
    details: {
      uiSample: 'culture'
    }
  },
  {
    id: "workplace-deepdive",
    title: "Workplace Deep Dive",
    component: WorkplaceDeepDive,
    details: {
      uiSample: 'efficiency'
    }
  },
  {
    id: "ai-companion",
    title: "AI Companion",
    component: AICompanionSlide,
    details: {
      uiSample: 'ai-companion'
    }
  },
  {
    id: "user-journey",
    title: "User Journey",
    component: UserJourneySlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "video-transition",
    title: "Video Transition",
    component: JIMIVideoTransition,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "crowdsourcing",
    title: "Crowdsourcing",
    component: CrowdsourcingSlide,
    details: {
      uiSample: 'crowdsourcing'
    }
  },
  {
    id: "governance",
    title: "Governance",
    component: GovernanceSlide,
    details: {
      uiSample: 'governance'
    }
  },
  {
    id: "data-science",
    title: "Data Science",
    component: DataScienceSlide,
    details: {
      uiSample: 'governance'
    }
  },
  {
    id: "implementation",
    title: "Implementation",
    component: ImplementationSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  // {
  //   id: "timeline",
  //   title: "Timeline",
  //   component: TimelineSlide,
  //   details: {
  //     uiSample: 'resilience'
  //   }
  // },
  {
    id: "budget",
    title: "Budget",
    component: BudgetSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "roi",
    title: "Return on Investment",
    component: ROISlide,
    details: {
      uiSample: 'resilience'
    }
  },
  // {
  //   id: "adoption-roi",
  //   title: "Adoption ROI",
  //   component: AdoptionROISlide,
  //   details: {
  //     uiSample: 'resilience'
  //   }
  // },
  {
    id: "cost-benefit-conclusion",
    title: "Cost Benefit Conclusion",
    component: CostBenefitConclusionSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "next-steps",
    title: "Next Steps",
    component: NextStepsSlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "qa",
    title: "Q&A",
    component: QASlide,
    details: {
      uiSample: 'resilience'
    }
  },
  {
    id: "thank-you",
    title: "Thank You",
    component: ThankYouSlide,
    details: {
      uiSample: 'resilience'
    }
  }
];

const StakeholderDeck: React.FC<StakeholderDeckProps> = ({
  onBackToWelcome,
  isWelcomeVisible = true,
  presentationStarted = false,
  startingSlideId = null,
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const { showCaptions, toggleCaptions } = useCaptions();
  const { isPlaying, isMuted, togglePlayback, toggleMute, playAudioForSlide, stopAllAudio } = useAudio();
  const { user } = useUser();
  const navigation = useNavigation();

  // Initialize slide state
  useEffect(() => {
    if (startingSlideId) {
      const slideIndex = slides.findIndex(slide => slide.id === startingSlideId);
      if (slideIndex !== -1) {
        console.log('[StakeholderDeck] Setting initial slide:', startingSlideId);
        setCurrentSlideIndex(slideIndex);
        // Don't play audio immediately - let the slide component handle it
      }
    }
  }, [startingSlideId]);

  // Handle slide changes
  const handleSlideChange = useCallback((newIndex: number) => {
    console.log('[StakeholderDeck] Changing slide to index:', newIndex);
    
    if (newIndex < 0 || newIndex >= slides.length) {
      console.warn('[StakeholderDeck] Invalid slide index:', newIndex);
      return;
    }
    
    // Set transitioning state
    setIsTransitioning(true);
    
    // Stop current audio
    stopAllAudio();
    
    // Update slide index
    setCurrentSlideIndex(newIndex);
    
    // Reset transitioning state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500); // Match animation duration
  }, [stopAllAudio]);

  // Handle next slide
  const handleNextSlide = useCallback(() => {
    if (currentSlideIndex < slides.length - 1) {
      handleSlideChange(currentSlideIndex + 1);
    }
  }, [currentSlideIndex, handleSlideChange]);

  // Handle previous slide
  const handlePrevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      handleSlideChange(currentSlideIndex - 1);
    }
  }, [currentSlideIndex, handleSlideChange]);

  const handleSlideSelect = useCallback((index: number) => {
    handleSlideChange(index);
  }, [handleSlideChange]);

  // Handle opening help dialog
  const handleOpenHelp = useCallback(() => {
    setShowKeyboardHelp(true);
  }, []);

  // Handle closing help dialog
  const handleCloseHelp = useCallback(() => {
    setShowKeyboardHelp(false);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === ' ') {
        handleNextSlide();
      } else if (event.key === 'ArrowLeft') {
        handlePrevSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNextSlide, handlePrevSlide]);

  // Render current slide with props
  const renderCurrentSlide = useCallback(() => {
    const currentSlide = slides[currentSlideIndex];
    const CurrentSlideComponent = currentSlide.component;

    return (
      <motion.div
        key={currentSlide.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CurrentSlideComponent
          isSpeaking={isPlaying}
          showCaptions={showCaptions}
          captionsText=""
          onNext={handleNextSlide}
          onPrevious={handlePrevSlide}
          onOpenJIMIChat={() => {}}
          onHelp={handleOpenHelp}
          details={currentSlide.details}
          mode="deck"
          onStart={() => {}}
          onNavigateToSlide={(slideId: string) => {
            const slideIndex = slides.findIndex(slide => slide.id === slideId);
            if (slideIndex !== -1) {
              handleSlideChange(slideIndex);
            }
          }}
          isTransitioning={isTransitioning}
        />
      </motion.div>
    );
  }, [currentSlideIndex, isPlaying, showCaptions, handleNextSlide, handlePrevSlide, handleOpenHelp, handleSlideChange, isTransitioning]);

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      console.log('[StakeholderDeck] Cleaning up on unmount');
      stopAllAudio();
    };
  }, [stopAllAudio]);

  return (
    <Box position="relative" width="100%" height="100vh" overflow="hidden">
      <AnimatePresence mode="wait">
        {renderCurrentSlide()}
      </AnimatePresence>

      {/* Slide Controls */}
      <SlideControls
        currentSlide={currentSlideIndex}
        totalSlides={slides.length}
        onPrevious={handlePrevSlide}
        onNext={handleNextSlide}
        onSlideSelect={handleSlideSelect}
        isSpeaking={isPlaying}
        onToggleSpeech={togglePlayback}
        showCaptions={showCaptions}
        onToggleCaptions={toggleCaptions}
        isWelcomeScreen={false}
        isMuted={isMuted}
        toggleMute={toggleMute}
        slides={slides}
        onHome={onBackToWelcome || (() => {})}
        onHelp={handleOpenHelp}
      />

      {/* Help Dialog */}
      {showKeyboardHelp && (
        <KeyboardShortcutsHelp 
          isOpen={showKeyboardHelp}
          onClose={handleCloseHelp} 
        />
      )}

      {/* JIMI Chat */}
      {/* <JIMIChat
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        slideData={slides}
        currentSlideIndex={currentSlideIndex}
      /> */}
    </Box>
  );
};

export default StakeholderDeck;
